import { privateApi } from '@/config/axios.config';
import { RecurringInvoice, InvoiceTemplate, RecurrenceFrequency } from '../types/recurring-invoice.types';

const API_BASE = '/condominium/recurring-invoices';

export interface RecurringInvoiceCreateDTO {
  name: string;
  frequency: RecurrenceFrequency;
  startDate: string;
  endDate?: string;
  dayOfMonth: number;
  amount: number;
  description?: string;
  invoiceTemplate: InvoiceTemplate;
  unitIds?: string[];
  isActive?: boolean;
}

export interface RecurringInvoiceUpdateDTO extends Partial<RecurringInvoiceCreateDTO> {
  id?: string;
}

export interface RecurringInvoiceFilters {
  isActive?: boolean;
  frequency?: RecurrenceFrequency;
  search?: string;
  page?: number;
  limit?: number;
}

export const recurringInvoicesApiService = {
  // Obtener todas las facturas recurrentes
  async getRecurringInvoices(filters: RecurringInvoiceFilters = {}): Promise<RecurringInvoice[]> {
    const params = new URLSearchParams();

    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.frequency) params.append('frequency', filters.frequency);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}?${params.toString()}`);
    
    // El interceptor global ResponseTransformInterceptor envuelve la respuesta en:
    // { success: true, data: <datos>, timestamp, path, method, ... }
    // Por lo tanto, los datos siempre están en response.data.data
    
    let data: any[] = [];
    
    // El interceptor siempre envuelve en { success: true, data: ... }
    // Entonces los datos están en response.data.data
    if (response.data && typeof response.data === 'object') {
      // Caso 1: Ya viene envuelto por el interceptor { success: true, data: [...] }
      if (response.data.success !== undefined && Array.isArray(response.data.data)) {
        data = response.data.data;
      }
      // Caso 2: Si data es directamente un array (sin interceptor, aunque no debería pasar)
      else if (Array.isArray(response.data)) {
        data = response.data;
      }
      // Caso 3: Si data es un objeto con una propiedad que es un array
      else if (response.data.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      }
      // Caso 4: Buscar cualquier propiedad que sea un array
      else if (typeof response.data === 'object') {
        for (const key of Object.keys(response.data)) {
          if (Array.isArray(response.data[key])) {
            data = response.data[key];
            break;
          }
        }
      }
    }
    
    // Mapear y normalizar los datos, filtrando valores null/undefined
    if (Array.isArray(data) && data.length > 0) {
      const mapped = data
        .filter((invoice: any) => {
          const isValid = invoice !== null && invoice !== undefined && typeof invoice === 'object';
          return isValid;
        })
        .map((invoice: any, index: number) => {
          // Helper para convertir fechas
          const formatDate = (date: any): string | null => {
            if (!date) return null;
            if (typeof date === 'string') {
              // Si ya es string, verificar si es válida
              try {
                new Date(date);
                return date;
              } catch {
                return null;
              }
            }
            // Si es objeto Date
            if (date instanceof Date) {
              return date.toISOString();
            }
            // Si tiene método toISOString
            if (typeof date.toISOString === 'function') {
              return date.toISOString();
            }
            // Si es número (timestamp)
            if (typeof date === 'number') {
              return new Date(date).toISOString();
            }
            // Intentar convertir a string y luego a Date
            try {
              const dateStr = String(date);
              const parsed = new Date(dateStr);
              if (!isNaN(parsed.getTime())) {
                return parsed.toISOString();
              }
            } catch {
              // Ignorar errores
            }
            return null;
          };

          const mappedInvoice = {
            id: invoice.id || '',
            name: invoice.name || 'Sin nombre',
            frequency: invoice.frequency || 'monthly',
            startDate: formatDate(invoice.startDate) || '',
            endDate: formatDate(invoice.endDate) || null,
            dayOfMonth: typeof invoice.dayOfMonth === 'number' ? invoice.dayOfMonth : (invoice.dayOfMonth ? parseInt(String(invoice.dayOfMonth), 10) : 1),
            amount: typeof invoice.amount === 'number' ? Number(invoice.amount) : (invoice.amount ? parseFloat(String(invoice.amount)) : 0),
            description: invoice.description || null,
            invoiceTemplate: invoice.invoiceTemplate || { items: [] },
            unitIds: Array.isArray(invoice.unitIds) ? invoice.unitIds : (invoice.unitIds ? [invoice.unitIds] : null),
            isActive: typeof invoice.isActive === 'boolean' ? invoice.isActive : (invoice.isActive !== undefined ? String(invoice.isActive) === 'true' : true),
            lastGeneratedDate: formatDate(invoice.lastGeneratedDate) || null,
            createdAt: formatDate(invoice.createdAt) || '',
            updatedAt: formatDate(invoice.updatedAt) || '',
          };
          
          return mappedInvoice;
        });
      
      return mapped;
    }
    
    return [];
  },

  // Obtener una factura recurrente por ID
  async getRecurringInvoiceById(id: string): Promise<RecurringInvoice> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data?.data || response.data;
  },

  // Crear nueva factura recurrente
  async createRecurringInvoice(data: RecurringInvoiceCreateDTO): Promise<RecurringInvoice> {
    const response = await privateApi.post(`${API_BASE}`, data);
    return response.data?.data || response.data;
  },

  // Actualizar factura recurrente
  async updateRecurringInvoice(id: string, data: RecurringInvoiceUpdateDTO): Promise<RecurringInvoice> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, data);
    return response.data?.data || response.data;
  },

  // Eliminar factura recurrente
  async deleteRecurringInvoice(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  // Generar facturas para hoy (ejecución manual)
  async generateInvoices(): Promise<{ success: boolean; generated: number; errors: Array<{ recurringInvoiceId: string; error: string }> }> {
    const response = await privateApi.post(`${API_BASE}/generate`);
    return response.data?.data || response.data;
  },
};

export default recurringInvoicesApiService;
